import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import ClientForm from "../../components/forms/ClientForm";
import { useClientsInfinite } from "../../hooks/useClientsInfinite";
import { useDebounce } from "../../hooks/useDebounce";
import {
  Client,
  ClientCreateFormData,
  ClientUpdateFormData,
} from "../../schemas/clientSchema";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { getChangedFields } from "../../utils/formUtils";

const ClientList: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const {
    clients,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createClient,
    updateClient,
    deleteClients,
  } = useClientsInfinite({ search: debouncedSearch || undefined });
  const { canEdit, canDelete, canCreate, canView } =
    useAuthPermissions("clients");

  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleDelete = (client: Client) => {
    if (window.confirm(`Are you sure you want to delete ${client.name}?`)) {
      deleteClients([client.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${ids.length} client(s)?`
      )
    ) {
      deleteClients(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = (data: ClientCreateFormData | ClientUpdateFormData) => {
    const closeModal = () => {
      setIsModalOpen(false);
      setSelectedClient(null);
    };

    if (selectedClient) {
      // Only send changed fields for update
      const changedFields = getChangedFields(data as ClientUpdateFormData, {
        name: selectedClient.name,
        email: selectedClient.email,
        phoneNumber: selectedClient.phoneNumber || "",
        restaurantName: selectedClient.restaurantName || "",
        screenIds:
          selectedClient.screenIds ||
          selectedClient.screens?.map((s) => s.id) ||
          [],
      });
      updateClient(
        {
          id: selectedClient.id,
          client: changedFields,
        },
        {
          onSuccess: closeModal,
        },
      );
    } else {
      createClient(data as ClientCreateFormData, {
        onSuccess: closeModal,
      });
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
  };

  const columns: ColumnDef<Client>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phoneNumber", header: "Phone Number" },
    { accessorKey: "restaurantName", header: "Restaurant Name" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
      visible: canEdit,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      visible: canDelete,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Clients</h1>
        {canCreate && (
          <button
            onClick={() => {
              setSelectedClient(null);
              setIsModalOpen(true);
            }}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
          >
            <PlusIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {canView && (
        <Table
          data={clients as Client[]}
          columns={columns}
          actions={actions}
          searchable={true}
          onSearch={setSearch}
          pagination={true}
          isLoading={isLoading}
          maxHeight="67vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          selectable={canDelete}
          getRowId={(client) => client.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            canDelete
              ? [
                  {
                    label: "Delete Selected",
                    icon: <TrashIcon className="h-4 w-4" />,
                    onClick: handleBulkDelete,
                    variant: "danger" as const,
                  },
                ]
              : undefined
          }
        />
      )}

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title={selectedClient ? "Edit Client" : "Create Client"}
          size="full"
        >
          <ClientForm
            initialData={selectedClient || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
            isUpdate={selectedClient !== null}
          />
        </Modal>
      )}
    </div>
  );
};

export default ClientList;
